import makeCreateFacilityMarker from '@PROJECT_ROOT/assets/desktop/js/search/mapsearch/FacilityMarker';
import makeShowFacilityDetails from '@PROJECT_ROOT/assets/mobile/js/mapSearch/FacilityDetails';

export default function createFacilityPoiHandler(google, map, facilityMarkerData, mapIconURLs) {
    const fetchResults = makeFetchResults(
        map,
        facilityMarkerData.searchParameters,
        facilityMarkerData.mapSearchResultsURL,
    );
    const googleMarkers = [];
    let lastDisplayedMarkers = [];

    return {
        showMarkers() {
            fetchResults().then((markers) => {
                removeMarkersFromMap(googleMarkers);
                lastDisplayedMarkers = showMarkersOnMap(
                    google,
                    map,
                    markers,
                    mapIconURLs,
                    facilityMarkerData,
                );
                googleMarkers.push(...lastDisplayedMarkers);
            }).catch((error) => {
                if (process.env.NODE_ENV !== 'production' && error.statusText !== 'abort') {
                    throw error;
                }
            });
        },
        hideMarkers() {
            removeMarkersFromMap(googleMarkers);
        },
    };
}

function makeFetchResults(map, searchParameters, mapSearchResultsURL) {
    let filterFacilityMarkersRequest = null;

    return () => {
        if (filterFacilityMarkersRequest) {
            filterFacilityMarkersRequest.abort();
        }

        return new Promise((resolve, reject) => {
            const filterParameters = getFilterParameters(searchParameters, map);
            filterFacilityMarkersRequest = $.post(mapSearchResultsURL, filterParameters);
            filterFacilityMarkersRequest.then((facilityMarkers) => {
                resolve(facilityMarkers);
            }, (error) => {
                reject(error);
            });
        });
    };
}

function getFilterParameters(searchParameters, map) {
    const bounds = map.getBounds();
    const filterParameters = Object.keys(searchParameters).map(
        (parameterName) => ({ name: parameterName, value: searchParameters[parameterName] }),
    );

    filterParameters.push({ name: 'disableClusters', value: map.getZoom() > 14 ? 1 : 0 });

    if (bounds === null) {
        return filterParameters;
    }

    return filterParameters.map((parameter) => {
        if (parameter.name === 'ne_lat') {
            return { ...parameter, value: bounds.getNorthEast().lat() };
        }

        if (parameter.name === 'ne_lng') {
            return { ...parameter, value: bounds.getNorthEast().lng() };
        }

        if (parameter.name === 'sw_lat') {
            return { ...parameter, value: bounds.getSouthWest().lat() };
        }

        if (parameter.name === 'sw_lng') {
            return { ...parameter, value: bounds.getSouthWest().lng() };
        }

        return parameter;
    });
}

function showMarkersOnMap(google, map, markers, mapIconURLs, facilityMarkerData) {
    const {
        facilityMarkerInfoURL,
        searchParameters,
        facilityId,
        facilityIconURL,
        facilityActiveIconURL,
    } = facilityMarkerData;
    const createFacilityMarker = makeCreateFacilityMarker({
        google,
        googleMap: map,
        facilityIcon: facilityIconURL,
        activeFacilityIcon: facilityActiveIconURL,
        facilityPriceSmallIcon: mapIconURLs.facilitySmallPriceIconURL,
        activeFacilityPriceSmallIcon: mapIconURLs.activeFacilitySmallPriceIconURL,
        facilityPriceMediumIcon: mapIconURLs.facilityMediumPriceIconURL,
        activeFacilityPriceMediumIcon: mapIconURLs.activeFacilityMediumPriceIconURL,
    });
    const showFacilityDetails = makeShowFacilityDetails(map, facilityMarkerInfoURL, searchParameters, google);

    let googleMapsFacilityMarkers = [];
    googleMapsFacilityMarkers = markers.map((marker) => {
        if (marker.count) {
            return createMarkerFromCluster(google, map, marker, mapIconURLs.clusterIconURL, mapIconURLs.clusterActiveIconURL);
        }

        return marker.id !== parseInt(facilityId, 10)
            ? createFacilityMarker(marker, onFacilityMarkerClick.bind(null, showFacilityDetails, () => googleMapsFacilityMarkers))
            : null;
    });

    return googleMapsFacilityMarkers;
}

function createMarkerFromCluster(google, map, cluster, clusterIconURL, clusterActiveIconURL) {
    const marker = new google.maps.Marker({
        map,
        icon: clusterIconURL,
        position: {
            lat: parseFloat(cluster.location.latitude),
            lng: parseFloat(cluster.location.longitude),
        },
        label: {
            color: '#000',
            fontSize: '12px',
            fontWeight: '600',
            text: String(cluster.count),
        },
    });

    marker.highlight = () => {
        marker.setIcon(clusterActiveIconURL);
    };
    marker.removeHighlight = () => {
        marker.setIcon(clusterIconURL);
    };

    marker.addListener('mouseover', () => {
        marker.highlight();
    });
    marker.addListener('mouseout', () => {
        marker.removeHighlight();
    });

    marker.addListener('click', () => {
        const southwest = cluster.southwesternmostCorner;
        const northeast = cluster.northeasternmostCorner;
        const bounds = new google.maps.LatLngBounds(
            new google.maps.LatLng(southwest.latitude, southwest.longitude),
            new google.maps.LatLng(northeast.latitude, northeast.longitude),
        );

        map.fitBounds(bounds);
    });

    return marker;
}

function removeMarkersFromMap(googleMarkers) {
    googleMarkers.forEach((marker) => {
        if (marker === null) {
            return;
        }

        marker.setMap(null);
    });
}

function onFacilityMarkerClick(showFacilityDetails, getGoogleMapsFacilityMarkers, clickedMarker) {
    showFacilityDetails({
        marker: clickedMarker,
        facilityId: clickedMarker.facilityId,
    });

    getGoogleMapsFacilityMarkers().forEach((marker) => {
        if (marker === null) {
            return;
        }

        marker.removeHighlight();
    });

    clickedMarker.highlight();
}
